import React, { useContext, useRef, useState } from 'react';
import { Copy } from 'assets/svg';
import { toBlob } from 'html-to-image';
import { Modal, Spin, Popover } from 'antd';
import styled from '@emotion/styled';
import { blobToBase64, calculateFontSize, getAdditionalDataOnCopyClick, getConfig } from 'utils/helperMethods';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { HIDE_WIDGET_ICON_CLASS } from 'config/constants';

interface Props {
    refOfComponent: React.RefObject<HTMLElement>;
    mixpanelEvent?: Function;
    removeScrollbar?: boolean;
    elementClassToRemoveScrollbar?: string;
    extraData?: Record<string, string[]>;
    chartOnly?: boolean; // New flag to copy only chart without filters
}

const PopoverStyled = styled(Popover)`
    .ant-popover-inner-content {
        padding: 7px 14px;
    }
`;
export const classToRemoveScrollbar = 'copy-widget-remove-scrollbar';

const popUpWidth = calculateFontSize(window.innerWidth, 600);

function CopyToClipBoard({
    refOfComponent,
    mixpanelEvent = undefined,
    removeScrollbar = false,
    elementClassToRemoveScrollbar,
    extraData,
    chartOnly = false, // Default to false for backward compatibility
}: Props) {
    const { allCheckedAttribute, timeSelectionLabel, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);

    const ModalStyled = styled(Modal)`
        .ant-modal-content {
            border-radius: 10px;
        }
    `;

    const [open, setOpen] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const imageRef = useRef<any>();
    const additionalDataRef = useRef<any>();

    const onButtonClick = async () => {
        setOpen(true);
        setShowContent(false);
        if (mixpanelEvent) mixpanelEvent();

        if (refOfComponent.current === null) return;

        const className = elementClassToRemoveScrollbar ?? classToRemoveScrollbar;
        let scrollbarHideElement: HTMLCollectionOf<HTMLElement> | null = null;
        let widgetHideElements: HTMLCollectionOf<HTMLElement> | null = null;
        let scrollbarCssText = '';
        let widgetCssTextList: string[] = [];

        // Shift the inner scroll container up by -scrollTop via translateY to bring
        // the currently-visible rows into frame.
        const scrollAdjustments: { innerEl: HTMLElement; prevTransform: string }[] = [];
        refOfComponent.current.querySelectorAll<HTMLElement>('*').forEach((el) => {
            if (el.scrollTop > 0 && el.children.length > 0) {
                const innerEl = el.children[0] as HTMLElement;
                const prevTransform = innerEl.style.transform;
                const extra = prevTransform && prevTransform !== 'none' ? ` ${prevTransform}` : '';
                innerEl.style.transform = `translateY(${-el.scrollTop}px)${extra}`;
                scrollAdjustments.push({ innerEl, prevTransform });
            }
        });

        if (removeScrollbar) {
            scrollbarHideElement = refOfComponent.current.getElementsByClassName(
                className,
            ) as HTMLCollectionOf<HTMLElement>;
            if (scrollbarHideElement?.length > 0) {
                scrollbarCssText = scrollbarHideElement[0].style.cssText;
                scrollbarHideElement[0].style.overflow = 'hidden';
            }
        }
        // Store CSS for all widget-icon elements and hide them
        widgetHideElements = refOfComponent.current?.getElementsByClassName(
            HIDE_WIDGET_ICON_CLASS,
        ) as HTMLCollectionOf<HTMLElement>;
        if (widgetHideElements?.length > 0) {
            for (let i = 0; i < widgetHideElements.length; i++) {
                widgetCssTextList[i] = widgetHideElements[i].style.cssText;
                widgetHideElements[i].style.visibility = 'hidden';
            }
        }

        const restoreScrollAdjustments = () => {
            scrollAdjustments.forEach(({ innerEl, prevTransform }) => {
                innerEl.style.transform = prevTransform;
            });
        };

        try {
            const imgBlob = await toBlob(refOfComponent.current, {
                backgroundColor: '#FFFFFF',
                height: refOfComponent.current?.clientHeight,
            });

            if (removeScrollbar && scrollbarHideElement?.length && scrollbarHideElement.length > 0) {
                scrollbarHideElement[0].style.cssText = scrollbarCssText;
            }

            // Restore CSS for all widget-icon elements
            if (widgetHideElements?.length > 0) {
                for (let i = 0; i < widgetHideElements.length; i++) {
                    widgetHideElements[i].style.cssText = widgetCssTextList?.[i];
                }
            }

            restoreScrollAdjustments();

            const imageBase64Data = await blobToBase64(imgBlob);
            imageRef.current = imageBase64Data;

            const additionalData = getAdditionalDataOnCopyClick(
                allCheckedAttribute,
                timeSelectionLabel,
                timePeriodSelection,
                commonConfig,
                extraData,
            );
            additionalDataRef.current = additionalData;

            setShowContent(true);
        } catch (err) {
            restoreScrollAdjustments();
            console.error('Error generating image:', err);
            setOpen(false);
        }
    };

    const handleCopy = async (withFilters: boolean) => {
        const html = `
            <div><img src="${imageRef.current}" alt="Copied image" /></div>
            ${withFilters ? additionalDataRef.current : ''}
        `;

        const htmlBlob = new Blob([html], { type: 'text/html' });

        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': htmlBlob,
            }),
        ]);

        setOpen(false);
        imageRef.current = null;
        additionalDataRef.current = null;
        setShowContent(false);
    };

    return (
        <>
            <PopoverStyled
                placement="bottom"
                trigger="hover"
                showArrow={false}
                content={<p className="font-xxs">Copy</p>}
                overlayInnerStyle={{ position: 'relative', top: -10, borderRadius: '5px', padding: '0px' }}
            >
                <div
                    id="copy-widget-icon"
                    onClick={onButtonClick}
                    className={`${HIDE_WIDGET_ICON_CLASS} h-fit cursor-pointer`}
                >
                    <Copy />
                </div>
            </PopoverStyled>

            <ModalStyled
                centered
                destroyOnClose
                open={open}
                footer={null}
                width={popUpWidth}
                onCancel={() => {
                    setOpen(false);
                    imageRef.current = null;
                    additionalDataRef.current = null;
                    setShowContent(false);
                }}
            >
                {showContent ? (
                    <>
                        <div className="font-xs font-medium mb-3">Preview</div>
                        <img src={imageRef.current} style={{ width: popUpWidth }} />
                        {!chartOnly && (
                            <div
                                className="mt-4"
                                style={{ maxHeight: 120, overflowY: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: additionalDataRef.current }}
                            />
                        )}
                        <div className="w-full flex justify-end mt-6 gap-4">
                            <div
                                className="font-xs min-h-[2.5rem] px-3 py-2 flex bg-purpleFont items-center rounded cursor-pointer text-white"
                                onClick={() => handleCopy(false)}
                            >
                                {chartOnly ? 'Copy Chart' : 'Copy Chart Only'}
                            </div>
                            {!chartOnly && (
                                <div
                                    className="font-xs min-h-[2.5rem] px-3 py-2 flex bg-purpleFont items-center rounded cursor-pointer text-white"
                                    onClick={() => handleCopy(true)}
                                >
                                    Copy Chart + Filters
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex justify-center items-center mt-8">
                        <Spin />
                    </div>
                )}
            </ModalStyled>
        </>
    );
}

export default CopyToClipBoard;
